import { ArrowLeft } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Wordmark } from '@/components/Wordmark';

/**
 * The sign-in pages in the website's theme (uncutgems.com): a pure black ground; strips of black-and-white wedding
 * photographs torn top and bottom, run edge to edge and dimmed right down; a brighter strip behind the wordmark with
 * an ember rule along its top edge; film grain and a vignette over all of it; the "// client portal" header; and a
 * way back to the website above it.
 * Login, Forgot password, Reset password and Verify email render inside it. Nothing here reads or changes what they
 * do: this is how they look, and the few class strings their forms share.
 *
 * The tear is a photograph of torn paper used as a mask (public/site/torn-*.png, copied from the website). Those PNGs
 * carry the paper in their ALPHA channel. The JPEG versions read as luminance masks would be simpler, but Safari
 * before its most recent versions ignores mask-mode and reads every mask as alpha; a JPEG has none, so on those
 * iPhones every tear drew as a plain rectangle (the website moved to the PNGs for that reason).
 */
const PHOTOS = Array.from({ length: 12 }, (_, i) => `/site/photos/wedding-${String(i + 1).padStart(2, '0')}.jpg`);
/** The website. The way back to it is a plain link at the top left, where the website's own nav starts, and the
 * wordmark, as on most sign-in pages; the brand bar's "Uncut Gems" stays a label (it names the brand you are on). */
const SITE = 'https://uncutgems.com';

function tear(asset) {
  const url = `url(/site/${asset}.png)`;
  return {
    WebkitMaskImage: url, maskImage: url, maskMode: 'alpha',
    WebkitMaskSize: '100% 150%', maskSize: '100% 150%',
    WebkitMaskPosition: 'center', maskPosition: 'center',
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
  };
}

/** One torn strip. It runs past both edges of the page (118% wide from -9%), so no end ever shows, and opens on a
 * different photograph (`start`) from its neighbours. The twelve photographs make about 14 strip-heights of width,
 * short of a wide screen's 118%, so the set runs three times: the strip is never shorter than the page. */
function Strip({ top, height, rotate, start, asset = 'torn-a', brightness = 0.2, ember = false, centered = false }) {
  const once = [...PHOTOS.slice(start), ...PHOTOS.slice(0, start)];
  const photos = [...once, ...once, ...once];
  const transform = `${centered ? 'translateY(-50%) ' : ''}rotate(${rotate}deg)`;
  return (
    <div aria-hidden="true" className="absolute" style={{ top, left: '-9%', width: '118%', height, transform }}>
      <div className="absolute inset-0 flex overflow-hidden"
           style={{ ...tear(asset), filter: `grayscale(1) contrast(1.3) brightness(${brightness})` }}>
        {photos.map((src, i) => <img key={`${i}-${src}`} src={src} alt="" draggable={false} className="h-full w-auto max-w-none flex-none object-cover" />)}
      </div>
      {ember && <div className="absolute left-0 right-0 bg-site-ember" style={{ top: '11%', height: 3 }} />}
    </div>
  );
}

const GRAIN = {
  backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='.55'/></svg>\")",
  opacity: 0.07,
  mixBlendMode: 'screen',
};
const VIGNETTE = { background: 'radial-gradient(130% 95% at 50% 42%, transparent 45%, rgba(0,0,0,.7) 100%)' };

export function AuthShell({ children }) {
  return (
    <div className="relative min-h-screen bg-black overflow-x-hidden flex flex-col text-site-paper font-sans">
      {/* The dim strips, fixed behind the page, so they stay put while a long form scrolls */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
        <Strip top="36%" height="clamp(84px, 13vh, 140px)" rotate={-1.6} start={3} />
        <Strip top="60%" height="clamp(78px, 12vh, 130px)" rotate={2.2} start={7} asset="torn-b" />
        <Strip top="83%" height="clamp(84px, 13vh, 140px)" rotate={-2.6} start={10} />
      </div>
      {/* The link's 44px row stands in for the header's top padding, so the page below moves 8px on a phone, none on a desktop */}
      <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10">
        <a href={SITE} data-testid="auth-back-to-site"
           className="inline-flex items-center gap-2 h-11 text-sm tracking-wide text-zinc-400 hover:text-site-teal focus-visible:text-site-teal focus-visible:outline-none transition-colors">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to uncutgems.com
        </a>
      </div>
      <header className="relative sm:pt-1 pb-8 sm:pb-10 text-center">
        <p className="relative font-azeret text-[10px] uppercase tracking-[0.3em] text-site-ember">// client portal</p>
        {/* The brighter strip is laid on the wordmark's own box, so it stays behind it at every width */}
        <div className="relative mt-4">
          <Strip top="50%" height="max(150%, 96px)" rotate={-1.2} start={0} brightness={0.42} ember centered />
          <h1 className="relative">
            <a href={SITE} aria-label="Uncut Gems website" data-testid="auth-wordmark-link" className="tap-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-site-teal">
              <Wordmark size="clamp(48px, 7vw, 96px)" />
            </a>
          </h1>
        </div>
      </header>
      <div className="relative flex-1 flex flex-col">{children}</div>
      {/* Film grain and a soft vignette over everything; neither takes a click */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={GRAIN} />
      <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={VIGNETTE} />
    </div>
  );
}

/** The sign-in forms' shared pieces, so the four pages cannot drift apart. */
export const AUTH_PANEL = 'bg-site-panel border border-site-line';
export const AUTH_LABEL = 'font-azeret text-[10px] font-normal uppercase tracking-[0.2em] text-site-muted';
export const AUTH_INPUT = 'bg-transparent border-0 border-b border-site-rule rounded-none text-site-paper placeholder:text-white/35 focus:border-site-teal focus-visible:border-site-teal h-11 px-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0';
export const AUTH_BUTTON = 'w-full h-12 rounded-none bg-site-teal text-site-ink hover:bg-site-teal/90 font-barlow font-bold text-[15px] uppercase tracking-[0.12em] shadow-none';
export const AUTH_LINK = 'font-barlow font-semibold uppercase tracking-[0.1em] text-site-teal underline decoration-2 decoration-site-teal underline-offset-4 hover:text-site-paper transition-colors';
export const AUTH_HEADING = 'font-barlow font-bold uppercase tracking-[0.08em] text-site-paper';

/** A field label; a required one carries its asterisk in ember. The words read exactly as they did ("Email *"). */
export function AuthLabel({ children, required = false, className = '' }) {
  return (
    <Label className={`block ${AUTH_LABEL} ${className}`}>
      {children}{required && <> <span className="text-site-ember">*</span></>}
    </Label>
  );
}
